import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import s from "./ComplianceIframe.module.scss";
import { useAuth } from "utils/useAuth/useAuth";
import { refreshTokenPromise } from "utils/api";

const config = require(`../../../config/${process.env.REACT_APP_ENV}/env.json`);

/**
 * Frames the compliance monitoring platform (FastAPI + React SPA, served from
 * its own origin) and hands it the portal session over postMessage.
 *
 * The compliance app owns its internal navigation, so one tab is enough —
 * `path` only picks the landing screen ("/", "/queue", "/cases", ...).
 *
 * The token is posted rather than put in the URL: a query string lands in
 * browser history, CloudFront access logs and the Referer header, and this
 * token is a bearer credential for the whole portal.
 */
const ComplianceIframe = ({ path = "/" }) => {
  const { refreshToken, user, token } = useAuth();

  const iframeRef = useRef(null);
  const [isInit, setIsInit] = useState(true);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isRefreshTokenPromiseResolved, setIsRefreshTokenPromiseResolved] =
    useState(!refreshTokenPromise);
  const isTokenExpired = user.exp < Date.now() / 1000;

  /**
   * Edge case handling — same as the operation/onboarding iframes.
   * Entering the tab with an expired but refreshable token would otherwise
   * inject the stale token, so hold the iframe in loading state until the
   * refresh settles.
   */
  useEffect(() => {
    const tokenPromiseHandler = async () => {
      if (isTokenExpired && !refreshTokenPromise) {
        refreshToken();
      }
      if (refreshTokenPromise) {
        await refreshTokenPromise;
      }
      setIsRefreshTokenPromiseResolved(true);
    };
    tokenPromiseHandler();
  }, []);

  const isAwaitingTokenRefresh =
    isTokenExpired || !isRefreshTokenPromiseResolved;

  const injectToken = useCallback(() => {
    const receiver = iframeRef.current?.contentWindow;
    if (!receiver) return;
    receiver.postMessage(
      {
        type: "session",
        payload: {
          session: { isoAgent: user, token },
          portalChanges: isInit,
          url: path,
        },
      },
      // Origin only — never "*", or any framed page could read the token.
      `${config.COMPLIANCE}`,
    );
  }, [user, token, path, isInit]);

  // Re-inject on every refresh so a long-lived tab never holds a dead token.
  useEffect(() => {
    if (isInit || !token) return;
    injectToken();
  }, [user, token, path]);

  const IframeOnLoadHandler = () => {
    injectToken();

    setTimeout(() => {
      setIsIframeLoaded(true);
      setIsInit(false);
    }, 1500);
  };

  return (
    <div className={s.outerContainer}>
      <div className={s.innerContainer}>
        {!isAwaitingTokenRefresh && (
          <iframe
            id={`compliance-iframe-${path}`}
            title="Compliance monitoring"
            src={config.COMPLIANCE + path}
            ref={iframeRef}
            onLoad={IframeOnLoadHandler}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              borderRadius: "4px",
              display: isIframeLoaded ? "block" : "none",
            }}
          >
            <p>Your browser does not support iframes.</p>
          </iframe>
        )}
        {(isAwaitingTokenRefresh || !isIframeLoaded) && (
          <div className={s.spinning}>
            <div className={s.spinningIcon}>
              <CircularProgress />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceIframe;
